import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

const AREA_COLORS = {
  health: '#4caf7d', mind: '#9b5ce0', career: '#c9a84c',
  relationships: '#e05c5c', faith: '#5c8fe0', purpose: '#e07d3c',
}

const AREA_OPTIONS = [
  { key: 'health', label: 'Health' }, { key: 'mind', label: 'Mind' },
  { key: 'career', label: 'Career' }, { key: 'relationships', label: 'Relationships' },
  { key: 'faith', label: 'Faith' }, { key: 'purpose', label: 'Goals' },
]

function formatDeadline(date) {
  if (!date) return ''
  const d = new Date(date)
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

function todayStr() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
}

export default function GoalsTab({ userId, showAdd, onAddClose }) {
  const [goals, setGoals] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedGoal, setSelectedGoal] = useState(null)
  const [inputValue, setInputValue] = useState(0)
  const [saving, setSaving] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  const [newStart, setNewStart] = useState('')
  const [newGoal, setNewGoal] = useState('')
  const [newDeadline, setNewDeadline] = useState(todayStr())
  const [newArea, setNewArea] = useState('purpose')
  const [addSaving, setAddSaving] = useState(false)

  useEffect(() => { if (userId) loadGoals() }, [userId])

  useEffect(() => {
    if (showAdd) {
      setNewTitle(''); setNewStart(''); setNewGoal('')
      setNewDeadline(todayStr()); setNewArea('purpose')
    }
  }, [showAdd])

  async function loadGoals() {
    const { data } = await supabase.from('goals').select('*')
      .eq('user_id', userId).order('created_at', { ascending: false })
    setGoals(data || [])
    setLoading(false)
  }

  function openGoal(goal) { setSelectedGoal(goal); setInputValue(goal.current_value) }

  function getPercent(goal) {
    const range = goal.goal_value - goal.start_value
    if (range === 0) return 100
    const progress = goal.current_value - goal.start_value
    return Math.min(100, Math.max(0, Math.round((progress / range) * 100)))
  }

  async function logProgress() {
    if (!selectedGoal) return
    setSaving(true)
    const newValue = Number(inputValue)
    const { data, error } = await supabase
      .from('goals')
      .update({ current_value: newValue })
      .eq('id', selectedGoal.id)
      .select()
      .single()
    if (data && !error) {
      setGoals(prev => prev.map(g => g.id === data.id ? data : g))
    }
    setSaving(false)
    setSelectedGoal(null)
  }

  async function saveNewGoal() {
    if (!newTitle.trim() || !newGoal) return
    setAddSaving(true)
    const { data } = await supabase.from('goals').insert({
      user_id: userId,
      title: newTitle.trim(),
      area: newArea,
      start_value: Number(newStart) || 0,
      goal_value: Number(newGoal),
      current_value: Number(newStart) || 0,
      deadline: newDeadline || null,
    }).select().single()
    if (data) setGoals(prev => [data, ...prev])
    setAddSaving(false)
    onAddClose?.()
  }

  async function deleteGoal(goalId) {
    await supabase.from('goals').delete().eq('id', goalId)
    setGoals(prev => prev.filter(g => g.id !== goalId))
    setSelectedGoal(null)
  }

  if (loading) return <div style={s.empty}>Loading...</div>

  return (
    <>
      {goals.length === 0 ? (
        <div style={s.emptyWrap}>
          <div style={s.emptyIcon}>🎯</div>
          <div style={s.emptyTitle}>No Goals Yet</div>
          <div style={s.emptyText}>Tap "Start Goal" from the + menu to add your first goal.</div>
        </div>
      ) : (
        <div style={s.list}>
          {goals.map(goal => {
            const pct = getPercent(goal)
            const color = AREA_COLORS[goal.area] || '#c9a84c'
            return (
              <div key={goal.id} style={s.goalCard} onClick={() => openGoal(goal)}>
                <div style={s.goalTop}>
                  <div style={s.goalLeft}>
                    <div style={{ ...s.goalIcon, color }}>🎯</div>
                    <div>
                      <div style={s.goalTitle}>{goal.title}</div>
                      {goal.deadline && <div style={s.goalDeadline}>{formatDeadline(goal.deadline)}</div>}
                    </div>
                  </div>
                  <div style={{ ...s.pctBadge, background: `${color}22`, color }}>{pct}% Complete</div>
                </div>
                <div style={s.barBg}>
                  <div style={{ ...s.barFill, width: `${pct}%`, background: color }} />
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* PROGRESS POPUP - centered with blur */}
      {selectedGoal && (
        <div style={s.popupOverlay} onClick={() => setSelectedGoal(null)}>
          <div style={s.popup} onClick={e => e.stopPropagation()}>
            <div style={s.popupHandle} />
            <div style={s.sheetHeader}>
              <div style={{ flex: 1 }}>
                <div style={s.sheetTitle}>{selectedGoal.title}</div>
                {selectedGoal.deadline && <div style={s.sheetDeadline}>📅 {formatDeadline(selectedGoal.deadline)}</div>}
              </div>
              <div style={s.iconBtn} onClick={() => deleteGoal(selectedGoal.id)}>✕</div>
            </div>
            <div style={s.sheetPct}>
              <div style={{ ...s.pctBadge, background: 'rgba(201,168,76,0.15)', color: '#c9a84c', fontSize: '14px', padding: '6px 16px' }}>
                {getPercent(selectedGoal)}% Complete
              </div>
            </div>
            <div style={{ ...s.barBg, marginBottom: '24px' }}>
              <div style={{ ...s.barFill, width: `${getPercent(selectedGoal)}%`, background: '#c9a84c', transition: 'width 0.4s ease' }} />
            </div>
            <div style={s.valRow}>
              <div style={s.valBox}>
                <div style={s.valLabel}>Starting</div>
                <div style={s.valNum}>{selectedGoal.start_value}</div>
              </div>
              <div style={s.valBox}>
                <div style={s.valLabel}>Goal</div>
                <div style={s.valNum}>{selectedGoal.goal_value}</div>
              </div>
            </div>
            <div style={s.inputRow}>
              <div style={s.adjBtn} onClick={() => setInputValue(v => Math.max(selectedGoal.start_value, Number((Number(v) - 1).toFixed(1))))}>−</div>
              <input
                style={s.currentVal}
                type="number"
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
              />
              <div style={s.adjBtn} onClick={() => setInputValue(v => Math.min(selectedGoal.goal_value, Number((Number(v) + 1).toFixed(1))))}>+</div>
            </div>
            <div style={{ ...s.logBtn, opacity: saving ? 0.6 : 1 }} onClick={logProgress}>
              {saving ? 'Saving...' : '+ Log Progress'}
            </div>
          </div>
        </div>
      )}

      {/* ADD GOAL BOTTOM SHEET */}
      {showAdd && (
        <>
          <div style={s.overlay} onClick={onAddClose} />
          <div style={{ ...s.sheet, paddingBottom: '48px' }}>
            <div style={s.sheetHandle} />
            <div style={s.addHeader}>
              <div style={s.iconBtn} onClick={onAddClose}>✕</div>
              <div style={s.addTitle}>ADD GOAL</div>
              <div style={{ ...s.saveBtn, opacity: addSaving || !newTitle.trim() || !newGoal ? 0.4 : 1 }}
                onClick={saveNewGoal}>{addSaving ? 'Saving...' : 'Save'}</div>
            </div>

            <div style={s.fieldLabel}>TITLE</div>
            <input style={s.input} value={newTitle}
              onChange={e => setNewTitle(e.target.value)} />

            <div style={s.fieldLabel}>STARTING VALUE</div>
            <input style={s.input} type="number" value={newStart}
              onChange={e => setNewStart(e.target.value)} />

            <div style={s.fieldLabel}>GOAL VALUE</div>
            <input style={s.input} type="number" value={newGoal}
              onChange={e => setNewGoal(e.target.value)} />

            <div style={s.fieldLabel}>TARGET DATE</div>
            <input style={s.input} type="date" value={newDeadline}
              onChange={e => setNewDeadline(e.target.value)} />

            <div style={s.fieldLabel}>LIFE AREA</div>
            <select style={s.select} value={newArea} onChange={e => setNewArea(e.target.value)}>
              {AREA_OPTIONS.map(a => (
                <option key={a.key} value={a.key}>{a.label}</option>
              ))}
            </select>
          </div>
        </>
      )}
    </>
  )
}

const s = {
  list: { display: 'flex', flexDirection: 'column', gap: '12px' },
  goalCard: { background: '#1a1a26', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', padding: '16px 18px', cursor: 'pointer' },
  goalTop: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', gap: '10px' },
  goalLeft: { display: 'flex', alignItems: 'center', gap: '12px', flex: 1 },
  goalIcon: { fontSize: '22px', flexShrink: 0 },
  goalTitle: { fontSize: '15px', fontWeight: '600', color: '#e8e6df', marginBottom: '2px' },
  goalDeadline: { fontSize: '12px', color: '#6b6860' },
  pctBadge: { fontSize: '12px', fontWeight: '700', padding: '5px 12px', borderRadius: '20px', whiteSpace: 'nowrap', flexShrink: 0 },
  barBg: { height: '5px', background: 'rgba(255,255,255,0.07)', borderRadius: '3px', overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: '3px', transition: 'width 0.6s ease' },
  emptyWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 24px', textAlign: 'center', gap: '12px' },
  emptyIcon: { fontSize: '48px' },
  emptyTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '24px', letterSpacing: '2px', color: '#e8e6df' },
  emptyText: { fontSize: '14px', color: '#6b6860', lineHeight: '1.6', maxWidth: '260px' },
  empty: { textAlign: 'center', color: '#6b6860', padding: '40px 0' },
  popupOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' },
  popup: { background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '24px', padding: '24px', width: '100%', maxWidth: '400px' },
  popupHandle: { width: '36px', height: '4px', background: 'rgba(255,255,255,0.15)', borderRadius: '2px', margin: '0 auto 20px' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 300 },
  sheet: { position: 'fixed', bottom: 0, left: 0, right: 0, background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '24px 24px 0 0', padding: '12px 24px 48px', zIndex: 301, maxHeight: '90vh', overflowY: 'auto' },
  sheetHandle: { width: '36px', height: '4px', background: 'rgba(255,255,255,0.15)', borderRadius: '2px', margin: '0 auto 20px' },
  sheetHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' },
  sheetTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '26px', letterSpacing: '2px', color: '#e8e6df', lineHeight: 1 },
  sheetDeadline: { fontSize: '13px', color: '#6b6860', marginTop: '4px' },
  iconBtn: { width: '36px', height: '36px', borderRadius: '50%', background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', color: '#6b6860', cursor: 'pointer', flexShrink: 0 },
  sheetPct: { display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' },
  valRow: { display: 'flex', gap: '12px', marginBottom: '20px' },
  valBox: { flex: 1, background: 'rgba(255,255,255,0.05)', borderRadius: '12px', padding: '14px', textAlign: 'center' },
  valLabel: { fontSize: '11px', letterSpacing: '1.5px', textTransform: 'uppercase', color: '#6b6860', marginBottom: '6px' },
  valNum: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', color: '#e8e6df' },
  inputRow: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '24px', marginBottom: '24px' },
  adjBtn: { width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', color: '#e8e6df', cursor: 'pointer', fontWeight: '300', userSelect: 'none', flexShrink: 0 },
  currentVal: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '52px', color: '#f0cc7a', lineHeight: 1, width: '160px', textAlign: 'center', background: 'transparent', border: 'none', borderBottom: '2px solid rgba(240,204,122,0.3)', outline: 'none', padding: '0 8px' },
  logBtn: { width: '100%', padding: '16px', background: 'linear-gradient(135deg, #c9a84c, #8b6a1a)', color: '#000', fontFamily: "'Bebas Neue', sans-serif", fontSize: '20px', letterSpacing: '2px', borderRadius: '14px', textAlign: 'center', cursor: 'pointer' },
  addHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' },
  addTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '32px', letterSpacing: '3px', color: '#e8e6df', textAlign: 'center', flex: 1 },
  saveBtn: { background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c', fontSize: '14px', fontWeight: '600', padding: '8px 18px', borderRadius: '20px', cursor: 'pointer', whiteSpace: 'nowrap' },
  fieldLabel: { fontSize: '13px', letterSpacing: '2px', textTransform: 'uppercase', color: '#6b6860', marginBottom: '6px', marginTop: '14px', fontWeight: '600' },
  input: { width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '11px 14px', color: '#e8e6df', fontSize: '15px', fontFamily: "'DM Sans', sans-serif", outline: 'none', boxSizing: 'border-box' },
  select: { width: '100%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '11px 14px', color: '#e8e6df', fontSize: '15px', fontFamily: "'DM Sans', sans-serif", outline: 'none', boxSizing: 'border-box', appearance: 'none', WebkitAppearance: 'none' },
  areaRow: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  areaChip: { padding: '7px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: '500', cursor: 'pointer' },
}
